import { useEffect, useRef } from 'react';
import type { LiveEvent } from '@mcoverage/shared';

type Handler = (event: LiveEvent) => void;

/**
 * Subscribes to the server's SSE feed.
 *
 * EventSource reconnects on its own, so this only wires up teardown. The
 * handler is kept in a ref so callers do not have to memoise it.
 */
export function useLiveEvents(handler: Handler, enabled = true): void {
  const ref = useRef(handler);
  ref.current = handler;

  useEffect(() => {
    if (!enabled) return;
    if (typeof EventSource === 'undefined') return;

    let source: EventSource | null = null;
    let retry: ReturnType<typeof setTimeout> | null = null;
    let closed = false;

    const connect = () => {
      if (closed) return;
      source = new EventSource('/api/events');
      source.onmessage = (msg) => {
        try {
          ref.current(JSON.parse(msg.data) as LiveEvent);
        } catch {
          // Ignore malformed frames rather than tearing down the stream.
        }
      };
      source.onerror = () => {
        source?.close();
        source = null;
        // EventSource retries quickly on transient errors; back off a little so
        // a dead server does not produce a tight reconnect loop.
        retry = setTimeout(connect, 4000);
      };
    };

    connect();

    return () => {
      closed = true;
      if (retry) clearTimeout(retry);
      source?.close();
    };
  }, [enabled]);
}